import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllUserHistory } from '../services/apiService';
import './RequestHistory.css';

const RequestHistory = ({ onRequestSelect }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const hasFetchedRef = useRef(false);

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getAllUserHistory();
      
      if (response.success && response.data) {
        setRequests(response.data);
      }
    } catch (err) {
      console.error('Error fetching history:', err);
      setError('Failed to load request history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchHistory();
    }
  }, [fetchHistory]);

  const handleRequestClick = (requestId) => {
    if (onRequestSelect) {
      onRequestSelect();
    }
    
    navigate(`/result/${requestId}`, { state: { fromHistory: true } });
  };

  const formatPromptData = (request) => {
    const parts = [];
    
    const fieldsToShow = ['for', 'color', 'style', 'typography', 'size', 'mood', 'layout', 'material'];
    fieldsToShow.forEach(field => {
      const value = request[field];
      if (value !== null && value !== undefined) {
        if (Array.isArray(value) && value.length > 0) {
          parts.push(value.join(', '));
        } else if (typeof value === 'string' && value.trim().length > 0) {
          parts.push(value);
        }
      }
    });
    
    if (request.dynamicInput && typeof request.dynamicInput === 'object') {
      Object.values(request.dynamicInput).forEach(val => {
        if (val && String(val).trim()) {
          parts.push(String(val));
        }
      });
    }
    
    return parts.filter(Boolean).join(' • ');
  };

  if (loading) {
    return (
      <div className="request-history-loading">
        <div className="loading-spinner"></div>
        <p>Loading your requests...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="request-history-error">
        <p>{error}</p>
        <button onClick={fetchHistory}>Try Again</button>
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <div className="request-history-empty">
        <p>No requests found with sufficient details.</p>
      </div>
    );
  }

  return (
    <div className="request-history-container">
      <h2 className="request-history-title">Request History</h2>
      <div className="request-history-list">
        {requests.map((request) => (
          <div
            key={`request-${request.id}`}
            className="request-history-item"
            onClick={() => handleRequestClick(request.id)}
          >
            <div className="request-history-content">
              <div className="request-history-text">
                <h3 className="request-project-name">{request.projectName || 'Untitled Project'}</h3>
                <p className="request-scenario">{request.idea || 'No description'}</p>
                <p className="request-prompt-data">{formatPromptData(request)}</p>
              </div>
              <div className="request-history-image">
                {request.firstImageUrl ? (
                  <img 
                    src={request.firstImageUrl} 
                    alt={request.projectName || 'Generated image'}
                    loading="lazy"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.onerror = null;
                    }}
                  />
                ) : (
                  <div className="request-history-image-placeholder">
                    <span>No image</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RequestHistory;

